import { APIRoutes } from "@/api";
import { useJobsStore } from "@/stores/jobs-store";
import { SseFormatter } from "@/utils/sse-formatter";
import React from "react";
import { toast } from "sonner";
import { z } from "zod";

function tryParse<T extends z.ZodRawShape>(
  data: string,
  schema: z.ZodObject<T>,
) {
  try {
    console.log("👉  data:", data);
    const parsed = JSON.parse(data);
    console.log("👉  parsed:", parsed);
    return schema.parse(parsed);
  } catch (error) {
    console.log("👉  JobProgress::readStream  tryParse error:", error);
  }
}

export type JobMessageHandler<T> = (jobId: string, message: T) => void;

export function useJobProgressReader<T extends z.ZodRawShape>(
  jobId: string,
  schema: z.ZodObject<T>,
  messageHandler: JobMessageHandler<z.infer<typeof schema>>,
) {
  const readStream = React.useCallback(
    async (signal: AbortSignal) => {
      const response = await fetch(APIRoutes.JOB_PROGRESS, {
        method: "POST",
        headers: {
          "Content-Type": "text/event-stream",
        },
        body: JSON.stringify({ jobId }),
        signal,
      });

      if (!response.body) {
        toast.error(`An error occurred: ${response.statusText}`);
        return;
      }

      const reader = response.body
        .pipeThrough(new TextDecoderStream())
        .getReader();

      try {
        while (true) {
          const { done, value } = await reader.read();
          if (done) {
            break;
          }
          console.log("👉  JobProgress::readStream  value:", value);

          const eventData = SseFormatter.decode(value);
          if (!eventData) {
            continue;
          }

          const message = tryParse(eventData, schema);
          console.log("👉  JobProgress::readStream:", {
            message,
            eventData,
          });

          if (!message) {
            continue;
          }

          messageHandler?.(jobId, message);

          if (message?.status === "completed") {
            await reader.cancel("Job completed");
          }
        }
      } catch (err) {
        console.error("Failed to read stream: ", err);
      } finally {
        useJobsStore.getState().removeJob(jobId);
        reader.releaseLock();
      }
    },
    [jobId, schema, messageHandler],
  );

  return readStream;
}

export interface JobProgressProps {
  jobId: string;
}
